import { type PagyMeta, PagyPagination } from "@/components/pagy-pagination"
import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import AppLayout from "@/layouts/AppLayout"
import { Head, router, usePage } from "@inertiajs/react"
import { IconCircleCheck, IconCircleDashed, IconCircleX, IconClock, IconSearch, IconTrophy } from "@tabler/icons-react"
import { useState } from "react"

interface User {
  id: number
  name: string
  avatar_url: string | null
  seniority_title: string | null
}

interface Cycle {
  id: number
  display_name: string
  status: string
  cycle_period: string
  year: number
  start_date: string | null
  end_date: string | null
  overall_score: number | null
  promoted: boolean
  user: User
  review_count: number
  completed_reviews: number
  promotion_eligible: boolean
}

interface Props {
  [key: string]: unknown
  cycles: Cycle[]
  pagy: PagyMeta
  filters: {
    search?: string
    status?: string
    period?: string
  }
}

function statusIcon(status: string) {
  switch (status) {
    case "completed":
      return <IconCircleCheck className="h-4 w-4 text-green-600" />
    case "in_progress":
    case "reviews_completed":
    case "admin_review":
      return <IconClock className="h-4 w-4 text-yellow-600" />
    case "cancelled":
      return <IconCircleX className="h-4 w-4 text-red-600" />
    default:
      return <IconCircleDashed className="h-4 w-4 text-muted-foreground" />
  }
}

function statusVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "completed":
      return "default"
    case "in_progress":
    case "reviews_completed":
    case "admin_review":
      return "secondary"
    case "cancelled":
      return "destructive"
    default:
      return "outline"
  }
}

function statusLabel(status: string) {
  return status
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")
}

export default function AdminPerformanceReviewCyclesIndex() {
  const { cycles, pagy, filters } = usePage<Props>().props
  const [search, setSearch] = useState(filters.search || "")

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    router.get(
      "/admin/performance_review_cycles",
      {
        search: search || undefined,
        status: filters.status || undefined,
        period: filters.period || undefined,
      },
      { preserveState: true },
    )
  }

  function handleFilter(key: string, value: string | undefined) {
    router.get(
      "/admin/performance_review_cycles",
      {
        search: filters.search || undefined,
        status: key === "status" ? value : filters.status || undefined,
        period: key === "period" ? value : filters.period || undefined,
      },
      { preserveState: true },
    )
  }

  function handleBulkCreate() {
    const period = new Date().getMonth() < 6 ? "april" : "october"
    const year = new Date().getFullYear()
    if (
      confirm(`Create ${period.charAt(0).toUpperCase() + period.slice(1)} ${year} review cycles for all employees?`)
    ) {
      router.post("/performance_review_cycles/bulk_create", {
        period,
        year,
      })
    }
  }

  return (
    <>
      <Head title="Admin - Performance Review Cycles - Operations Center" />
      <div className="flex flex-1 flex-col gap-4 p-4 lg:p-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/admin">Admin</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Performance Review Cycles</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">Performance Review Cycles (Admin)</h1>
          <Button size="sm" onClick={handleBulkCreate}>
            Bulk Create for Current Period
          </Button>
        </div>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <form onSubmit={handleSearch} className="flex gap-2 sm:max-w-sm">
            <div className="relative flex-1">
              <IconSearch className="text-muted-foreground absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2" />
              <Input
                placeholder="Search by employee name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button type="submit" variant="outline">
              Search
            </Button>
          </form>

          <div className="flex items-center gap-2">
            <Select
              value={filters.status || "all"}
              onValueChange={(v) => handleFilter("status", v === "all" ? undefined : v)}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="not_started">Not Started</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="reviews_completed">Reviews Completed</SelectItem>
                <SelectItem value="admin_review">Admin Review</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filters.period || "all"}
              onValueChange={(v) => handleFilter("period", v === "all" ? undefined : v)}
            >
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="Period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Periods</SelectItem>
                <SelectItem value="april">April</SelectItem>
                <SelectItem value="october">October</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10" />
              <TableHead>Employee</TableHead>
              <TableHead>Cycle</TableHead>
              <TableHead>Level</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Reviews</TableHead>
              <TableHead>Score</TableHead>
              <TableHead>Promotion</TableHead>
              <TableHead className="w-24">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cycles.map((cycle) => (
              <TableRow key={cycle.id}>
                <TableCell>{statusIcon(cycle.status)}</TableCell>
                <TableCell className="font-medium">{cycle.user.name}</TableCell>
                <TableCell>
                  <a href={`/admin/performance_review_cycles/${cycle.id}`} className="text-primary hover:underline">
                    {cycle.display_name}
                  </a>
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">{cycle.user.seniority_title || "—"}</TableCell>
                <TableCell>
                  <Badge variant={statusVariant(cycle.status)}>{statusLabel(cycle.status)}</Badge>
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {cycle.completed_reviews}/{cycle.review_count}
                </TableCell>
                <TableCell>
                  {cycle.overall_score ? (
                    <span className="font-medium">{cycle.overall_score.toFixed(1)}</span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell>
                  {cycle.promoted ? (
                    <Badge variant="default" className="bg-green-600">
                      <IconTrophy className="mr-1 h-3 w-3" />
                      Promoted
                    </Badge>
                  ) : cycle.promotion_eligible ? (
                    <Badge variant="outline">Eligible</Badge>
                  ) : null}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" asChild>
                      <a href={`/admin/performance_review_cycles/${cycle.id}`}>View</a>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {cycles.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="text-muted-foreground text-center py-8">
                  No performance review cycles found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <PagyPagination pagy={pagy} />
      </div>
    </>
  )
}

AdminPerformanceReviewCyclesIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
